import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, cn } from '../lib/utils';
import { getApiErrorMessage, isEmailNotVerifiedError } from '../lib/apiError';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, Trash2, CalendarIcon, Loader2, ArrowLeft, Send, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

const EditInvoice = () => {
  const { invoiceId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [items, setItems] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState('custom');
  const [lateFeePercentage, setLateFeePercentage] = useState(0);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}`);
      const data = response.data;
      setInvoice(data);
      setItems(data.items.map((item) => ({ ...item, id: crypto.randomUUID() })));
      setDueDate(parseISO(data.due_date));
      setNotes(data.notes || '');
      setTaxRate(data.tax_rate || 0);
      setPaymentTerms(data.payment_terms || 'custom');
      setLateFeePercentage(data.late_fee_percentage || 0);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { id: crypto.randomUUID(), description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setItems(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dueDate) {
      toast.error('Please select a due date');
      return;
    }
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/invoices/${invoiceId}`, {
        items: items.map(({ id, ...item }) => item),
        due_date: format(dueDate, 'yyyy-MM-dd'),
        notes: notes || null,
        tax_rate: taxRate,
        payment_terms: paymentTerms,
        late_fee_percentage: lateFeePercentage
      });
      toast.success('Invoice updated successfully');
      navigate('/invoices');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update invoice'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndResend = async () => {
    if (!dueDate) {
      toast.error('Please select a due date');
      return;
    }
    if (items.some(item => !item.description || item.unit_price <= 0)) {
      toast.error('Please fill in all item details');
      return;
    }

    setSending(true);
    try {
      // First save the changes
      await api.put(`/invoices/${invoiceId}`, {
        items: items.map(({ id, ...item }) => item),
        due_date: format(dueDate, 'yyyy-MM-dd'),
        notes: notes || null,
        tax_rate: taxRate,
        payment_terms: paymentTerms,
        late_fee_percentage: lateFeePercentage
      });
      
      // Then resend the invoice (for sent invoices, use resend endpoint)
      if (invoice.status === 'sent' || invoice.status === 'viewed' || invoice.status === 'partially_paid' || invoice.status === 'overdue') {
        await api.post(`/invoices/${invoiceId}/resend`, {
          origin_url: window.location.origin
        });
        toast.success('Invoice updated and notification sent to client');
      } else {
        // For draft invoices, use the send endpoint
        await api.post(`/invoices/${invoiceId}/send`, {
          origin_url: window.location.origin
        });
        toast.success('Invoice saved and sent to client');
      }
      
      navigate(`/invoices/${invoiceId}`);
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        toast.error('Verify your email before sending invoices. Check your inbox for the verification link.', {
          action: {
            label: 'Resend email',
            onClick: async () => {
              try {
                await api.post('/auth/resend-verification');
                toast.success('Verification email sent — please check your inbox.');
              } catch (err) {
                toast.error(getApiErrorMessage(err, 'Could not send verification email.'));
              }
            },
          },
          duration: 8000,
        });
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to save and resend invoice'));
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </div>
      </Layout>
    );
  }

  if (!invoice) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="edit-invoice-page">
        <EmailVerificationBanner />
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">Edit Invoice</h1>
            <p className="text-muted-foreground mt-1">{invoice.invoice_number} - {invoice.client_name}</p>
          </div>
        </div>

        {/* Warning for editing sent invoices */}
        {invoice.status !== 'draft' && invoice.status !== 'paid' && (
          <Alert variant="warning" className="border-amber-500 bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-700">Editing a sent invoice</AlertTitle>
            <AlertDescription className="text-amber-600">
              This invoice has already been sent to the client. Making changes will create a new version and invalidate any pending payment links. 
              The client will need to be notified of the updated invoice.
            </AlertDescription>
          </Alert>
        )}

        {/* Error for paid invoices */}
        {invoice.status === 'paid' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Cannot edit paid invoice</AlertTitle>
            <AlertDescription>
              Paid invoices cannot be edited. If you need to make changes, please create a new invoice instead.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
              <CardDescription>Edit the items or services</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <div key={item.id} className="flex gap-4 items-end" data-testid={`line-item-${index}`}>
                  <div className="flex-1">
                    <Label>Description</Label>
                    <Input
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      data-testid={`item-description-${index}`}
                    />
                  </div>
                  <div className="w-24">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      data-testid={`item-quantity-${index}`}
                    />
                  </div>
                  <div className="w-32">
                    <Label>Unit Price ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      data-testid={`item-price-${index}`}
                    />
                  </div>
                  <div className="w-32 text-right">
                    <Label className="invisible">Total</Label>
                    <p className="font-mono font-medium py-2">
                      {formatCurrency(item.quantity * item.unit_price)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addItem} className="gap-2" data-testid="add-item-btn">
                <Plus className="w-4 h-4" />
                Add Item
              </Button>

              <div className="border-t pt-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="font-mono font-medium">{formatCurrency(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="tax-rate" className="text-sm text-muted-foreground whitespace-nowrap">Tax Rate</Label>
                    <div className="flex items-center">
                      <Input
                        id="tax-rate"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={taxRate}
                        onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                        className="w-20 text-right"
                        data-testid="tax-rate-input"
                      />
                      <span className="ml-1 text-muted-foreground">%</span>
                    </div>
                  </div>
                  <span className="font-mono">{formatCurrency(calculateTax())}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-medium">Total</span>
                  <span className="text-2xl font-heading font-bold">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Terms */}
              <div>
                <Label>Payment Terms</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger data-testid="payment-terms-select">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                    <SelectItem value="net_15">Net 15 (15 days)</SelectItem>
                    <SelectItem value="net_30">Net 30 (30 days)</SelectItem>
                    <SelectItem value="net_60">Net 60 (60 days)</SelectItem>
                    <SelectItem value="custom">Custom Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !dueDate && "text-muted-foreground"
                      )}
                      data-testid="due-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dueDate ? format(dueDate, "PPP") : "Select due date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={dueDate}
                      onSelect={setDueDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Late Fee */}
              <div>
                <Label htmlFor="lateFee">Late Fee (% per month)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="lateFee"
                    type="number"
                    min="0"
                    max="25"
                    step="0.5"
                    value={lateFeePercentage}
                    onChange={(e) => setLateFeePercentage(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="w-24"
                    data-testid="late-fee-input"
                  />
                  <span className="text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {lateFeePercentage > 0 
                    ? `${lateFeePercentage}% will be applied monthly on overdue invoices`
                    : 'Set to 0 for no late fees'}
                </p>
              </div>

              <div>
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add any notes for your client..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  data-testid="notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/invoices')} data-testid="cancel-btn">
              Cancel
            </Button>
            {invoice.status !== 'paid' && (
              <>
                <Button type="submit" disabled={saving || sending} data-testid="save-invoice-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                </Button>
                {invoice.status !== 'draft' && invoice.client_email && (
                  <Button 
                    type="button" 
                    onClick={handleSaveAndResend} 
                    disabled={saving || sending || !user?.email_verified}
                    title={!user?.email_verified ? 'Verify your email to unlock sending.' : undefined}
                    className="gap-2"
                    data-testid="save-resend-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Save & Resend
                  </Button>
                )}
              </>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default EditInvoice;
